// BM-H3756 / BM-H3634 / BM-H3879: what the fee earner has to confirm before a Will can go further.
//
// The confirmations facility was backend only, so nobody could record a confirmation. This is the screen:
// one row for each thing that needs confirming on THIS Will (a row only appears when it applies), who may
// confirm it, whether it counts now, and a form that asks for the evidence the firm requires. It records
// nothing itself: the server checks who is signed in, stamps the person and the date, and refuses a bare tick.
// A refusal is shown in the server's own words, including every missing piece, and the panel then shows what
// the server says is true, not what was typed.
import { useCallback, useEffect, useState } from "react";
import { clientFormsApi } from "../../api/clientForms";
import { errorMessage } from "../../utils/errorMessage";

export const ACTION_LABEL = {
  product_price_confirmed: "Confirm product and price",
  transferable_nrb_confirmed: "Verify the transferable nil-rate band",
  charity_identifier_verified: "Verify this charity's details",
  charity_purpose_performable: "Confirm a charity can carry out this purpose",
  trustees_uk_residence: "Confirm the Trustees live in the UK",
  earlier_wills_checked: "Confirm earlier wills have been checked",
  share_valuation_approach: "Confirm the share valuation approach",
  trust_corporation_status: "Confirm the Trust Corporation",
  earlier_wills_supervisor_approval: "Approve revoking all earlier wills",
  issue_membership_confirmed: "Record whether this person is within issue",   // BM-H3931
  different_people_confirmed: "Confirm these are two different people",        // BM-H3959 (ruling 39)
  life_tenant_remainder_reviewed: "Record the review of this life tenant who is also a remainderman",   // BM-H3959 (ruling 38)
  available_nrb_reviewed: "Confirm the available nil-rate band has been reviewed",   // BM-H3969
};

// BM-H3969: the typed "Available nil-rate band reviewed" form. Nothing is defaulted: a choice of "none" or "list" has to be made
// for each of the three items, and nothing is sent for an item until it is. The server refuses what is missing or malformed.
const NRB_EMPTY = {
  trust_mode: "", trust_items: [], gifts_mode: "", gift_items: [], exemptions: "", clts: false, pets: false, clt_notes: "",
  available_nrb: "", claimed_mode: "", claimed_amount: "", calculation_basis: "",
};
const NRB_TRUST_ITEM = { date: "", value_gbp: "", trust_details: "" };
const NRB_GIFT_ITEM = { date: "", value_gbp: "", recipient: "" };

// is_issue starts as "" (no answer): it is never defaulted, and nothing is sent for it until the fee earner chooses.
const EMPTY = { basis: "", evidence_reference: "", evidence_link: "", amount_gbp: "", deceased_party: "", competence_attested: false, is_issue: "", consequences_reviewed: false, ...NRB_EMPTY };

const asNumberOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

// The review fields of the request body (BM-H3969). An item whose none-or-list choice has not been made is not sent at all.
export function nrbReviewBody(f) {
  const body = {
    exemptions_and_reliefs_considered: f.exemptions.trim(), clts_considered: !!f.clts, pets_considered: !!f.pets,
    clt_pet_notes: f.clt_notes.trim(), calculation_basis: f.calculation_basis.trim(),
    available_nrb_gbp: asNumberOrNull(f.available_nrb),
  };
  if (f.trust_mode === "none") body.lifetime_trust_transfers = "none";
  else if (f.trust_mode === "list") body.lifetime_trust_transfers = f.trust_items.map((i) => ({ date: i.date.trim(), value_gbp: asNumberOrNull(i.value_gbp), trust_details: i.trust_details.trim() }));
  if (f.gifts_mode === "none") body.other_lifetime_gifts = "none";
  else if (f.gifts_mode === "list") body.other_lifetime_gifts = f.gift_items.map((i) => ({ date: i.date.trim(), value_gbp: asNumberOrNull(i.value_gbp), recipient: i.recipient.trim() }));
  if (f.claimed_mode === "none") body.transferable_nrb_claimed_gbp = "none";
  else if (f.claimed_mode === "amount") body.transferable_nrb_claimed_gbp = asNumberOrNull(f.claimed_amount);
  return body;
}

function NrbEntries({ keyPrefix, title, mode, items, blank, detailName, detailLabel, onMode, onItems, input }) {
  const setItem = (i, patch) => onItems(items.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  return (
    <fieldset className="text-xs text-slate-300" data-testid={`${keyPrefix}-fieldset`}>
      <legend>{title}</legend>
      <label className="mr-4"><input type="radio" name={`${keyPrefix}-mode`} checked={mode === "none"} onChange={() => onMode("none")} data-testid={`${keyPrefix}-none`} /> None</label>
      <label><input type="radio" name={`${keyPrefix}-mode`} checked={mode === "list"} onChange={() => { onMode("list"); if (items.length === 0) onItems([{ ...blank }]); }} data-testid={`${keyPrefix}-list`} /> Some (list each one)</label>
      {mode === "list" && (
        <div className="mt-2 space-y-2">
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-4 gap-2" data-testid={`${keyPrefix}-item-${i}`}>
              <input className={input} type="date" aria-label="Date" value={it.date} onChange={(e) => setItem(i, { date: e.target.value })} data-testid={`${keyPrefix}-item-${i}-date`} />
              <input className={input} type="number" min="0" step="0.01" aria-label="Value (£)" placeholder="Value (£)" value={it.value_gbp} onChange={(e) => setItem(i, { value_gbp: e.target.value })} data-testid={`${keyPrefix}-item-${i}-value`} />
              <input className={input} aria-label={detailLabel} placeholder={detailLabel} value={it[detailName]} onChange={(e) => setItem(i, { [detailName]: e.target.value })} data-testid={`${keyPrefix}-item-${i}-detail`} />
              <button type="button" className="text-xs underline" onClick={() => onItems(items.filter((_, j) => j !== i))} data-testid={`${keyPrefix}-item-${i}-remove`}>Remove</button>
            </div>
          ))}
          <button type="button" className="text-xs underline" onClick={() => onItems([...items, { ...blank }])} data-testid={`${keyPrefix}-add`}>Add another</button>
        </div>
      )}
    </fieldset>
  );
}

function NrbReviewFields({ k, f, setF, input }) {
  const set = (name) => (e) => setF((cur) => ({ ...cur, [name]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  return (
    <div className="space-y-3" data-testid={`${k}-nrb-review`}>
      <NrbEntries keyPrefix={`${k}-trust`} title="Lifetime transfers into a trust (with dates, values and trust details)" mode={f.trust_mode} items={f.trust_items}
                  blank={NRB_TRUST_ITEM} detailName="trust_details" detailLabel="Trust details" input={input}
                  onMode={(m) => setF((c) => ({ ...c, trust_mode: m }))} onItems={(items) => setF((c) => ({ ...c, trust_items: items }))} />
      <NrbEntries keyPrefix={`${k}-gifts`} title="Other relevant lifetime gifts (with dates, values and recipients)" mode={f.gifts_mode} items={f.gift_items}
                  blank={NRB_GIFT_ITEM} detailName="recipient" detailLabel="Recipient" input={input}
                  onMode={(m) => setF((c) => ({ ...c, gifts_mode: m }))} onItems={(items) => setF((c) => ({ ...c, gift_items: items }))} />
      <label className="block text-xs text-slate-300">Exemptions and reliefs considered
        <textarea className={input} rows={2} value={f.exemptions} onChange={set("exemptions")} data-testid={`${k}-exemptions`} />
      </label>
      <div className="text-xs text-slate-300 space-y-1">
        <label className="flex items-start gap-2"><input type="checkbox" checked={f.clts} onChange={set("clts")} data-testid={`${k}-clts`} /><span>I have considered chargeable lifetime transfers.</span></label>
        <label className="flex items-start gap-2"><input type="checkbox" checked={f.pets} onChange={set("pets")} data-testid={`${k}-pets`} /><span>I have considered potentially exempt transfers.</span></label>
      </div>
      <label className="block text-xs text-slate-300">Notes on the chargeable lifetime transfers and potentially exempt transfers considered
        <textarea className={input} rows={2} value={f.clt_notes} onChange={set("clt_notes")} data-testid={`${k}-clt-notes`} />
      </label>
      <label className="block text-xs text-slate-300">The testator's available nil-rate band (£)
        <input className={input} type="number" min="0" step="0.01" value={f.available_nrb} onChange={set("available_nrb")} data-testid={`${k}-available-nrb`} />
      </label>
      <fieldset className="text-xs text-slate-300" data-testid={`${k}-claimed-fieldset`}>
        <legend>Transferable nil-rate band claimed</legend>
        <label className="mr-4"><input type="radio" name={`${k}-claimed-mode`} checked={f.claimed_mode === "none"} onChange={() => setF((c) => ({ ...c, claimed_mode: "none" }))} data-testid={`${k}-claimed-none`} /> None claimed</label>
        <label><input type="radio" name={`${k}-claimed-mode`} checked={f.claimed_mode === "amount"} onChange={() => setF((c) => ({ ...c, claimed_mode: "amount" }))} data-testid={`${k}-claimed-amount-choice`} /> An amount is claimed</label>
        {f.claimed_mode === "amount" && (
          <input className={`${input} mt-2`} type="number" min="0" step="0.01" aria-label="Transferable nil-rate band claimed (£)" value={f.claimed_amount} onChange={set("claimed_amount")} data-testid={`${k}-claimed-amount`} />
        )}
      </fieldset>
      <label className="block text-xs text-slate-300">How the available nil-rate band was calculated
        <textarea className={input} rows={2} value={f.calculation_basis} onChange={set("calculation_basis")} data-testid={`${k}-calculation-basis`} />
      </label>
    </div>
  );
}

function whenText(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
}

// The server's refusal as lines of plain text: every problem it lists, or its message.
export function refusalLines(err) {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === "object") {
    const problems = Array.isArray(detail.problems) ? detail.problems.filter((p) => typeof p === "string") : [];
    if (problems.length) return problems;
    if (typeof detail.message === "string" && detail.message) return [detail.message];
  }
  return [errorMessage(err, "The confirmation was not recorded.")];
}

function bodyFor(row, f) {
  const body = { kind: row.kind, basis: f.basis.trim(), evidence_reference: f.evidence_reference.trim() };
  if (row.needs_link) body.evidence_link = f.evidence_link.trim();
  if (row.subject_id) body.subject_id = row.subject_id;
  if (row.needs_amount) body.amount_gbp = f.amount_gbp === "" ? null : Number(f.amount_gbp);
  if (row.needs_party) body.deceased_party = f.deceased_party.trim();
  if (row.needs_competence) body.competence_attested = !!f.competence_attested;
  // BM-H3931: the server needs a real true or false and refuses anything else, so an unanswered question is not sent.
  if (row.needs_issue_answer && (f.is_issue === "yes" || f.is_issue === "no")) body.is_issue = f.is_issue === "yes";
  // BM-H3959: only a ticked box is sent, as a real true. An unticked box sends nothing and the server says what is missing.
  if (row.needs_consequences_reviewed && f.consequences_reviewed === true) body.consequences_reviewed = true;
  if (row.needs_nrb_review) Object.assign(body, nrbReviewBody(f));        // BM-H3969
  return body;
}

// BM-H3959 (ruling 39): the two records that look like one person, side by side, as plain text. The fee earner reads both and
// then either confirms they are different people or removes the duplicate so that one remains.
function PairDetails({ rowKey, people, reasons }) {
  return (
    <div className="mt-2" data-testid={`will-confirmation-${rowKey}-pair`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {people.map((p, i) => (
          <dl key={p.id || i} className="border border-white/10 rounded p-2 text-xs text-slate-200" data-testid={`will-confirmation-${rowKey}-record-${i + 1}`}>
            <dt className="text-slate-400">Record {i + 1}</dt>
            <dd className="font-medium">{p.full_legal_name}</dd>
            <dd>Relationship: {p.relationship ? String(p.relationship).replace(/_/g, " ") : "not recorded"}</dd>
            <dd>Date of birth: {p.date_of_birth || "not recorded"}</dd>
            <dd>Address: {p.address || "not recorded"}</dd>
            <dd>Email: {p.email || "not recorded"}</dd>
          </dl>
        ))}
      </div>
      {Array.isArray(reasons) && reasons.length > 0 && (
        <p className="mt-1 text-xs text-slate-300" data-testid={`will-confirmation-${rowKey}-why`}>Why they look like one person: {reasons.join("; ")}.</p>
      )}
    </div>
  );
}

function Row({ row, matterRef, formInstanceId, onStatus, uatMatter }) {
  const key = `${row.kind}${row.subject_id ? `-${row.subject_id}` : ""}`;
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [problems, setProblems] = useState([]);
  const set = (name) => (e) => setF((cur) => ({ ...cur, [name]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async () => {
    setBusy(true);
    setProblems([]);
    try {
      const { data } = await clientFormsApi.recordConfirmation(matterRef, formInstanceId, bodyFor(row, f));
      onStatus(data.status, data.uat_matter);
      setOpen(false);
      setF(EMPTY);
    } catch (err) {
      setProblems(refusalLines(err));
    } finally {
      setBusy(false);
    }
  };

  const input = "w-full rounded border border-white/15 bg-white/5 px-2 py-1 text-sm text-white";
  return (
    <li className="border border-white/10 rounded-md p-3" data-testid={`will-confirmation-${key}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm text-white">{row.label}</span>
        <span className={`text-xs px-2 py-0.5 rounded border ${row.valid ? "border-emerald-500/40 text-emerald-300" : "border-amber-500/40 text-amber-300"}`}
              data-testid={`will-confirmation-${key}-state`}>
          {row.valid ? "Confirmed" : "Not confirmed"}
        </span>
      </div>
      {Array.isArray(row.people) && row.people.length === 2 ? (
        <PairDetails rowKey={key} people={row.people} reasons={row.reasons} />
      ) : (
        row.subject_detail && !row.affected_person && <p className="mt-1 text-xs text-slate-300" data-testid={`will-confirmation-${key}-subject`}>{row.subject_detail}</p>
      )}
      {row.affected_person && (
        <p className="mt-1 text-xs text-slate-200" data-testid={`will-confirmation-${key}-affected`}>
          Affected person: {row.affected_person.full_legal_name}. {row.subject_detail}
        </p>
      )}
      <p className="mt-1 text-xs text-slate-400">
        Who can confirm this: {row.permitted_confirmer}.
        {uatMatter && " This is a UAT matter, so anyone on the team can; it is recorded as taken under the UAT mark."}
      </p>
      {row.valid && row.taken_under_uat_mark && (
        <p className="mt-1 text-xs text-amber-300" data-testid={`will-confirmation-${key}-uat`}>
          Taken under the UAT mark by {row.confirmed_by || row.confirmed_by_email}, who is not necessarily the fee earner on
          this matter. It stops counting if the UAT mark is taken off.
        </p>
      )}
      {row.valid ? (
        <p className="mt-1 text-xs text-slate-300" data-testid={`will-confirmation-${key}-detail`}>
          Confirmed by {row.confirmed_by || row.confirmed_by_email} on {whenText(row.confirmed_at)}.
          {" "}Basis: {row.basis}. Evidence: {row.evidence_reference}.
          {row.amount_gbp != null && ` Amount: £${Number(row.amount_gbp).toLocaleString("en-GB")}.`}
          {row.deceased_party && ` From: ${row.deceased_party}.`}
          {row.is_issue === true && " Recorded: within issue."}
          {row.is_issue === false && " Recorded: not within issue."}
          {row.consequences_reviewed === true && " Recorded: the dispositive, tax and conflict consequences were reviewed."}
          {row.review && ` Available nil-rate band: £${Number(row.review.available_nrb_gbp).toLocaleString("en-GB")}. Transferable band claimed: ${row.review.transferable_nrb_claimed_gbp === "none" ? "none" : `£${Number(row.review.transferable_nrb_claimed_gbp).toLocaleString("en-GB")}`}.`}
        </p>
      ) : (
        <p className="mt-1 text-xs text-slate-400" data-testid={`will-confirmation-${key}-reason`}>{row.reason}</p>
      )}
      {!open && (
        <button type="button" onClick={() => setOpen(true)} className="mt-2 text-xs underline text-teal"
                data-testid={`will-confirmation-${key}-open`}>
          {row.valid ? "Confirm again" : (ACTION_LABEL[row.kind] || "Record confirmation")}
        </button>
      )}
      {open && (
        <div className="mt-3 space-y-2" data-testid={`will-confirmation-${key}-form`}>
          <label className="block text-xs text-slate-300">On what basis are you confirming this?
            <textarea className={input} rows={2} value={f.basis} onChange={set("basis")} data-testid={`will-confirmation-${key}-basis`} />
          </label>
          <label className="block text-xs text-slate-300">Where is the evidence held on the matter? (a file name, folder or note)
            <input className={input} value={f.evidence_reference} onChange={set("evidence_reference")} data-testid={`will-confirmation-${key}-reference`} />
          </label>
          {row.needs_link && (
            <label className="block text-xs text-slate-300">Link to the documentary evidence
              <input className={input} value={f.evidence_link} onChange={set("evidence_link")} data-testid={`will-confirmation-${key}-link`} />
            </label>
          )}
          {row.needs_amount && (
            <label className="block text-xs text-slate-300">Confirmed transferable amount (£)
              <input className={input} type="number" min="0" step="0.01" value={f.amount_gbp} onChange={set("amount_gbp")} data-testid={`will-confirmation-${key}-amount`} />
            </label>
          )}
          {row.needs_party && (
            <label className="block text-xs text-slate-300">Which deceased spouse or civil partner does it come from?
              <input className={input} value={f.deceased_party} onChange={set("deceased_party")} data-testid={`will-confirmation-${key}-party`} />
            </label>
          )}
          {row.needs_issue_answer && (
            <fieldset className="text-xs text-slate-300" data-testid={`will-confirmation-${key}-is-issue`}>
              <legend>Is this person within "issue" (a child or remoter descendant of the client)?</legend>
              <label className="mr-4"><input type="radio" name={`is-issue-${key}`} value="yes" checked={f.is_issue === "yes"} onChange={set("is_issue")}
                                             data-testid={`will-confirmation-${key}-is-issue-yes`} /> Yes, within issue</label>
              <label><input type="radio" name={`is-issue-${key}`} value="no" checked={f.is_issue === "no"} onChange={set("is_issue")}
                            data-testid={`will-confirmation-${key}-is-issue-no`} /> No, not within issue</label>
            </fieldset>
          )}
          {row.needs_consequences_reviewed && (
            <label className="flex items-start gap-2 text-xs text-slate-300">
              <input type="checkbox" checked={f.consequences_reviewed} onChange={set("consequences_reviewed")} data-testid={`will-confirmation-${key}-consequences`} />
              <span>I have reviewed the dispositive, tax and conflict consequences of this with the client.</span>
            </label>
          )}
          {row.needs_nrb_review && <NrbReviewFields k={`will-confirmation-${key}`} f={f} setF={setF} input={input} />}
          {row.needs_competence && (
            <label className="flex items-start gap-2 text-xs text-slate-300">
              <input type="checkbox" checked={f.competence_attested} onChange={set("competence_attested")} data-testid={`will-confirmation-${key}-competence`} />
              <span>I am the fee earner on this matter and competent to verify this tax position.</span>
            </label>
          )}
          <p className="text-xs text-slate-400">Your name and today's date are recorded automatically.</p>
          {problems.length > 0 && (
            <ul className="list-disc pl-5 text-xs text-status-red space-y-1" data-testid={`will-confirmation-${key}-problems`}>
              {problems.map((p) => <li key={p}>{p}</li>)}
            </ul>
          )}
          <div className="flex gap-2">
            <button type="button" onClick={submit} disabled={busy}
                    className="px-3 py-1 rounded bg-teal text-white text-sm disabled:opacity-60"
                    data-testid={`will-confirmation-${key}-submit`}>
              {busy ? "Recording..." : (ACTION_LABEL[row.kind] || "Record confirmation")}
            </button>
            <button type="button" onClick={() => { setOpen(false); setProblems([]); }} className="px-3 py-1 rounded border border-white/20 text-sm text-slate-200"
                    data-testid={`will-confirmation-${key}-cancel`}>Cancel</button>
          </div>
        </div>
      )}
    </li>
  );
}

const ANSWER_WORDS = { yes: "Yes", no: "No", not_asked: "Not asked" };

// BM-H3969 (solicitor's option B): what the client said to the two lifetime questions, shown on EVERY matter whatever structure
// they chose, read-only. Asks for no confirmation: only clause C-14c's own "Available nil-rate band reviewed" row does.
export function LifetimeInformation({ info }) {
  const items = Array.isArray(info?.items) ? info.items : [];
  if (items.length === 0) return null;
  return (
    <div className="mt-3 border border-white/10 rounded-md p-2" data-testid="will-lifetime-information">
      <h4 className="text-xs font-semibold text-white">What the client said about lifetime transfers and gifts</h4>
      <ul className="mt-1 space-y-1">
        {items.map((it) => (
          <li key={it.key} className="text-xs text-slate-200" data-testid={`will-lifetime-${it.key}`}>
            {it.question}{" "}
            <strong data-testid={`will-lifetime-${it.key}-answer`}>{ANSWER_WORDS[it.answer] || "Not asked"}</strong>
          </li>
        ))}
      </ul>
      {info.any_yes && (
        <p className="mt-1 text-xs text-amber-300" data-testid="will-lifetime-yes-note">
          The client answered yes. The fee earner must check the nil-rate band available to this client before relying on it.
        </p>
      )}
      {info.note && <p className="mt-1 text-xs text-slate-400" data-testid="will-lifetime-note">{info.note}</p>}
    </div>
  );
}

export default function WillConfirmationsPanel({ matterRef, formInstanceId, onRecorded }) {
  const [rows, setRows] = useState(null);
  const [info, setInfo] = useState(null);
  const [uatMatter, setUatMatter] = useState(false);
  const [loadError, setLoadError] = useState("");
  // Called only when a confirmation has just been recorded (BM-H4285: the matter page then says its draft result is older).
  const setStatus = (status, uat) => {
    setRows(status);
    if (typeof uat === "boolean") setUatMatter(uat);
    if (onRecorded) onRecorded();
  };

  const load = useCallback(async () => {
    setLoadError("");
    try {
      const { data } = await clientFormsApi.getConfirmations(matterRef, formInstanceId);
      setRows(Array.isArray(data?.status) ? data.status : []);
      setInfo(data?.client_information?.lifetime_transfers || null);
      setUatMatter(data?.uat_matter === true);
    } catch (err) {
      setRows(null);
      setInfo(null);
      setLoadError(refusalLines(err)[0]);
    }
  }, [matterRef, formInstanceId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="mt-4 border border-white/10 rounded-md p-3" data-testid="will-confirmations-panel">
      <h3 className="text-sm font-semibold text-white">Confirmations for this Will</h3>
      <p className="mt-1 text-xs text-slate-400">
        Things the Will depends on that the client cannot declare. A confirmation stops counting if the answers it
        is about change, and then it has to be given again.
      </p>
      <LifetimeInformation info={info} />
      {uatMatter && (
        <p className="mt-2 text-xs text-amber-300" data-testid="will-confirmations-uat-notice">
          UAT matter. Anyone on the team can take these steps. Each one is recorded as taken under the UAT mark, and none of
          them counts once the mark is taken off.
        </p>
      )}
      {loadError && (
        <p className="mt-2 text-xs text-status-red" data-testid="will-confirmations-error">
          Could not load the confirmations: {loadError}{" "}
          <button type="button" onClick={load} className="underline" data-testid="will-confirmations-retry">Try again</button>
        </p>
      )}
      {rows === null && !loadError && <p className="mt-2 text-xs text-slate-400">Loading...</p>}
      {rows && rows.length === 0 && <p className="mt-2 text-xs text-slate-400" data-testid="will-confirmations-empty">Nothing needs confirming yet.</p>}
      {rows && rows.length > 0 && (
        <ul className="mt-3 space-y-2">
          {rows.map((row) => (
            <Row key={`${row.kind}-${row.subject_id || ""}`} row={row} matterRef={matterRef}
                 formInstanceId={formInstanceId} onStatus={setStatus} uatMatter={uatMatter} />
          ))}
        </ul>
      )}
    </div>
  );
}
